import * as React from 'react';

import { getContactPersons, getManuals } from '../../../../shared/utils';
import { useEffect, useMemo, useState } from 'react';
import Badge from '@mui/material/Badge';
import { ChangelogModal, ChangelogVersion } from './ChangelogModal';

import { Avatar } from '@mui/material';
import Button from '@ingka/button';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SSRIcon from '@ingka/ssr-icon';
import capitalize from 'lodash/capitalize';
import documentIcon from '@ingka/ssr-icon/paths/document';
import logoutIcon from '@ingka/ssr-icon/paths/arrow-right-to-base';
import mailIcon from '@ingka/ssr-icon/paths/mail';
import noteIcon from '@ingka/ssr-icon/paths/note';
import nth from 'lodash/nth';
import personIcon from '@ingka/ssr-icon/paths/person';
import styled from '@emotion/styled';
import { tokens } from '../../../../shared/styles/core/tokens';
import { useMsal } from '@azure/msal-react';

interface ContactPerson {
   email: string;
}

const ContactMenuItem = () => {
   const [accessContact, setAccessContact] = useState<ContactPerson | null>(null);

   useEffect(() => {
      const fetchContact = () => {
         const contacts = getContactPersons();
         setAccessContact(contacts?.[0] || null);
      };

      fetchContact();
   }, []);

   if (!accessContact) return null;

   return (
      <StyledMenuItem key="contacts-item">
         <StyledFlexedMenuItemDiv>
            <StyledAnchor href={`mailto:${accessContact.email}`} target="_blank" rel="noreferrer">
               <SSRIcon paths={mailIcon} />
               <div>Access contact</div>
            </StyledAnchor>
         </StyledFlexedMenuItemDiv>
      </StyledMenuItem>
   );
};

const ManualMenuItem = () => {
   const [manual] = getManuals()

   return [
      (manual && <StyledMenuItem key="manual-item">
         <StyledFlexedMenuItemDiv>
            <StyledAnchor href={manual.href} target="_blank" rel="noreferrer">
               <SSRIcon paths={documentIcon} />
               <div>User guide</div>
            </StyledAnchor>
         </StyledFlexedMenuItemDiv>
      </StyledMenuItem>),
   ]
}

const CHANGELOG_LAST_SEEN_KEY = 'changelog-last-seen';

export const UserMenu = () => {
   const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
   const open = Boolean(anchorEl);
   const { instance, accounts } = useMsal();
   const [versions, setVersions] = useState<ChangelogVersion[]>([]);
   const [hasUnread, setHasUnread] = useState(false);
   const [changelogOpen, setChangelogOpen] = useState(false);

   const accountName = accounts[0]?.name;

   useEffect(() => {
      fetch('/changelog.json')
         .then((r) => (r.ok ? r.json() : []))
         .then((data: ChangelogVersion[]) => {
            setVersions(data);
            const lastSeen = localStorage.getItem(CHANGELOG_LAST_SEEN_KEY);
            setHasUnread(data.length > 0 && data[0].version !== lastSeen);
         })
         .catch(() => { });
   }, []);

   const handleOpenChangelog = () => {
      setChangelogOpen(true);
      handleClose();
      if (versions.length > 0) {
         localStorage.setItem(CHANGELOG_LAST_SEEN_KEY, versions[0].version);
         setHasUnread(false);
      }
   };

   const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
   };
   const handleClose = () => {
      setAnchorEl(null);
   };

   const initials = useMemo(
      () =>
         accountName
            ?.split(' ')
            .slice(0, 2)
            .map((str) => str.slice(0, 1))
            .join(''),
      [accountName]
   );

   return (
      <div>
         <Button text={accounts[0].name} type="tertiary" small={true} ssrIcon={personIcon} iconPosition={'leading'} onClick={handleClick} />
         {changelogOpen && <ChangelogModal versions={versions} onClose={() => setChangelogOpen(false)} />}
         <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            sx={{ borderRadius: '0px' }}
            elevation={4}
            MenuListProps={{
               'aria-labelledby': 'basic-button',
            }}
         >
            <StyledTopMenuDiv>
               <Avatar>{initials}</Avatar>
               <StyledNameAndRoleDiv>
                  <h4>{accounts[0].name}</h4>
                  <span>{capitalize(nth(accounts[0].idTokenClaims?.roles, 0))}</span>
               </StyledNameAndRoleDiv>
            </StyledTopMenuDiv>

            <Divider style={{ margin: `${tokens.space.sm} 0` }} />

            {ManualMenuItem()}
            {ContactMenuItem()}
            <StyledMenuItem onClick={handleOpenChangelog}>
               <StyledFlexedMenuItemDiv>
                  <StyledAnchor as="div">
                     <Badge color="error" variant="dot" invisible={!hasUnread}
                        sx={{ '& .MuiBadge-dot': { top: 2, right: 2 } }}
                     >
                        <SSRIcon paths={noteIcon} />
                     </Badge>
                     <div>What's new</div>
                  </StyledAnchor>
               </StyledFlexedMenuItemDiv>
            </StyledMenuItem>

            <Divider />

            <StyledMenuItem
               onClick={() => {
                  instance.logout();
                  handleClose();
               }}
            >
               <StyledMenuItemDiv>
                  <SSRIcon paths={logoutIcon} />
                  <StyledMenuItemSpan>Logout</StyledMenuItemSpan>
               </StyledMenuItemDiv>
            </StyledMenuItem>
         </Menu>
      </div>
   );
};

const StyledNameAndRoleDiv = styled.div`
   padding-left: ${tokens.space.md};
   display: block;
`;

const StyledTopMenuDiv = styled.li`
   display: flex;
   padding: ${tokens.space.md} ${tokens.space['3xl']} ${tokens.space.md} ${tokens.space.md};
   align-items: center;
`;

const StyledMenuItem = styled(MenuItem)`
   font-size: ${tokens.font.size.lg};
   font-family: ${tokens.font.family};
   display: flex;
   align-items: center;
`;

const StyledMenuItemSpan = styled.span`
   padding-left: ${tokens.space.xl};
`;

const StyledMenuItemDiv = styled.div`
   padding-left: ${tokens.space.sm};
`;

const StyledFlexedMenuItemDiv = styled(StyledMenuItemDiv)`
   display: flex;
   justify-content: space-between;
   width: 100%;
`

const StyledAnchor = styled.a`
  text-decoration: none !important;
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${tokens.space.xl};
`